import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../ui/dialog';
import { UserPlus, Trash, CheckCircle, XCircle, KeyReturn, Copy } from '@phosphor-icons/react';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const { user: caller } = useAuth();
  const isSuperadmin = caller?.role === 'superadmin';

  // The issued link, held only long enough to be copied. Never fetched back:
  // the server keeps a hash, so this is the one moment it is readable.
  const [resetLink, setResetLink] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);

  const issueResetLink = async (u) => {
    if (!window.confirm(
      `Issue a password reset link for No. ${String(u.member_no ?? '').padStart(3, '0')}?

`
      + 'Any link issued to them earlier stops working. Hand this one over in '
      + 'person or by phone — never inside Arcanum.'
    )) return;
    setResetBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/password-resets`, { user_id: u.id }, { withCredentials: true },
      );
      setResetCopied(false);
      setResetLink(data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not issue a reset link');
    } finally {
      setResetBusy(false);
    }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink.reset_url);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 2000);
    } catch (e) { /* clipboard denied — the link is on screen to read */ }
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    display_name: '',
  });

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, {
        withCredentials: true,
      });
      setUsers(data);
    } catch (error) {
      // Silent fail - loading state will clear, UI shows empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/users`, formData, {
        withCredentials: true,
      });
      setShowCreateDialog(false);
      setFormData({ email: '', password: '', name: '', display_name: '' });
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      const endpoint = isActive
        ? `${API_URL}/api/admin/users/${userId}/deactivate`
        : `${API_URL}/api/admin/users/${userId}/activate`;
      await axios.patch(endpoint, {}, { withCredentials: true });
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        withCredentials: true,
      });
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading users...</div>;
  }

  return (
    <div className="space-y-4" data-testid="user-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-semibold">Users</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {users.length} {isSuperadmin ? 'total' : 'assigned'} user{users.length === 1 ? '' : 's'}
          </p>
        </div>

        {isSuperadmin && (
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-user-button">
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Admin-controlled account creation. The user will login with these credentials.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name</Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  data-testid="user-display-name-input"
                />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="submit-create-user">
                Create User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">No.</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                {/* E-mail is deliberately not served here — see the Registry tab. */}
                <TableCell className="font-heading tabular-nums" style={{ color: 'hsl(var(--gold))' }}>
                  {user.member_no != null ? String(user.member_no).padStart(3, '0') : '—'}
                </TableCell>
                <TableCell>{user.display_name || user.name}</TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {user.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  {user.is_active ? (
                    <Badge variant="outline" className="border-green-500/20 bg-green-500/10 text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" weight="fill" />
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-destructive/20 bg-destructive/10 text-destructive">
                      <XCircle className="w-3 h-3 mr-1" weight="fill" />
                      Inactive
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {/* Founder only. The server refuses every one of these
                        to an operator, and a control that refuses is worse
                        than one that is absent: their view is read-only. */}
                    {isSuperadmin && user.role !== 'admin' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(user.id, user.is_active)}
                          data-testid={`toggle-user-${user.id}`}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        {isSuperadmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={resetBusy}
                            onClick={() => issueResetLink(user)}
                            title="Issue a password reset link"
                            data-testid={`reset-user-${user.id}`}
                          >
                            <KeyReturn className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Shown once. The server stores only a hash, so closing this is the
          last time anyone can read it. */}
      <Dialog open={!!resetLink} onOpenChange={(o) => { if (!o) setResetLink(null); }}>
        <DialogContent className="max-w-md" data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset link issued</DialogTitle>
            <DialogDescription>
              For {resetLink?.display_name}
              {resetLink?.member_no != null
                ? ` — No. ${String(resetLink.member_no).padStart(3, '0')}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div
              className="rounded-lg border border-border bg-muted/50 p-3 font-mono text-xs"
              style={{ overflowWrap: 'anywhere' }}
              data-testid="reset-link-value"
            >
              {resetLink?.reset_url}
            </div>

            <Button onClick={copyResetLink} className="w-full" variant="outline">
              <Copy className="w-4 h-4 mr-2" />
              {resetCopied ? 'Copied' : 'Copy the link'}
            </Button>

            <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">This is the only time it is shown.</strong>{' '}
                Only its hash is stored, so it cannot be looked up again — issue
                a new one if it is lost.
              </p>
              <p>
                Hand it over in person or by phone.{' '}
                <strong className="text-foreground">Never send it inside Arcanum</strong>,
                where anyone holding the account could already read it.
              </p>
              <p>
                They set their own password, so you will not know it. It expires
                in 12 hours and works once.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
